import type { DfBundleChildItem, DfCodeMapping, DfSellableProductRow, DfThirdPartyStockDetail, DfZoneBlock } from "./types";
import { DEFAULT_DF_SELLABLE_RATIO } from "./utils";

/** 列表项 — 子产品 / 组合 BOM */
export interface DfSellableListChildApi {
    productId?: string;
    sku1p?: string;
    /** 兼容字段：同 sku1p */
    skuIn1p?: string;
    productName?: string;
    /** @deprecated 组合配比数量请优先用 comboStockNum */
    qty?: number;
    /** 组合配比数量（件/套）← children[].comboStockNum */
    comboStockNum?: number;
    comboStockNumLabel?: string;
    type?: number;
    typeLabel?: string;
}

/** 三方仓 X — 海外仓 SKU 明细 */
export interface DfSellableListWarehouseChildApi {
    productId?: string;
    sku1p?: string;
    productName?: string;
    qty?: number;
    warehouseSku?: string;
    matchQty?: number;
    availableQty?: number;
    lockedQty?: number;
    matchedAvailableQty?: number;
}

/** 绑定三方仓 X */
export interface DfSellableListWarehouseApi {
    id?: string | number;
    externalWarehouseId?: string;
    systemWarehouseName?: string;
    warehouseSku?: string;
    matchQty?: number;
    availableQty?: number;
    lockedQty?: number;
    matchedAvailableQty?: number;
    warningStock?: number;
    /** 可售库存取百分比 ← warehouseCodes[].warehouses[].percent */
    percent?: number;
    customerStock?: number;
    customerUpdateTimeLabel?: string;
    matchSkuCount?: number;
    /** 组合库存模式：1 取组合产品库存；2 取子品库存 ← warehouses[].comboStockMode */
    comboStockMode?: number;
    comboStockModeLabel?: string;
    children?: DfSellableListWarehouseChildApi[];
}

/**
 * 同一 VC SKU 下的 Amazon DF 仓码（Y）
 * 操作（改 Y / 日志）请用本层 id，勿用组代表 id
 */
export interface DfSellableSuppressedTipItem {
    type?: string;
    tip?: string;
}

export interface DfSellableListWarehouseCodeApi {
    id: string;
    warehouseCode?: string;
    zoneCode?: string;
    zoneName?: string;
    platformStock?: number;
    warningStock?: number;
    /** @deprecated 可售库存百分比改取 warehouses[].percent */
    percent?: number;
    apiStatus?: number;
    apiStatusLabel?: string;
    updateFailReason?: string;
    platformUpdateTimeLabel?: string;
    lastPlatformPushAtLabel?: string;
    /** 仓码维度抑制 / 异常状态文案（仓库代码列第二行） */
    status?: string;
    /** 仓码维度 tip（单条）；多条优先 extractedSuppressedJson */
    tip?: string;
    suppressedText?: string;
    /** 仓码维度 tip 列表：type 作标题、tip 作正文（hover） */
    extractedSuppressedJson?: DfSellableSuppressedTipItem[];
    warehouses?: DfSellableListWarehouseApi[];
}

/**
 * POST /manage/amazonVcDfSellableStock/list 单条
 * 分页组：storeId + countryCode + asin + vcCode；id 为组内最大 Y id（代表行）
 */
export interface DfSellableListItemApi {
    id: string;
    corpId?: string;
    corpName?: string;
    storeId?: string;
    countryCode?: string;
    productId?: string;
    sku1p?: string;
    productName?: string;
    model?: string;
    productType?: number;
    productTypeLabel?: string;
    children?: DfSellableListChildApi[];
    asin?: string;
    /** 产品图片 URL */
    imageLink?: string;
    msku?: string;
    vcSku?: string;
    vcCode?: string;
    ownerUserId?: string;
    ownerUserName?: string;
    syncStatus?: number;
    syncStatusLabel?: string;
    syncBlockFlags?: number;
    /** 状态开关是否可操作：true 可点，false 禁用（兼容 1/0、"true"/"false"） */
    syncOperable?: boolean | number | string;
    strategyLevel?: number;
    statusRemark?: string;
    /** 兼容：抑制状态挂在产品组上时，展平到各仓码行 */
    status?: string;
    tip?: string;
    suppressedText?: string;
    extractedSuppressedJson?: DfSellableSuppressedTipItem[];
    /** 多仓码 / 多分区 */
    warehouseCodes?: DfSellableListWarehouseCodeApi[];
    /**
     * 兼容旧扁平结构（单区直接挂在 item 上）
     * @deprecated 新接口请用 warehouseCodes
     */
    warehouseCode?: string;
    zoneCode?: string;
    zoneName?: string;
    platformStock?: number;
    /** @deprecated 列表「预警库存」改取 warehouseCodes[].warehouses[].warningStock */
    warningStock?: number;
    /** @deprecated 可售库存百分比改取 warehouses[].percent */
    percent?: number;
    /** 组合库存模式：1 自身仓存；2 子品短板 */
    comboStockMode?: number;
    comboStockModeLabel?: string;
    apiStatus?: number;
    apiStatusLabel?: string;
    updateFailReason?: string;
    platformUpdateTimeLabel?: string;
    lastPlatformPushAtLabel?: string;
    warehouses?: DfSellableListWarehouseApi[];
}

/** apiStatus：0空闲 1待推送 2推送中 3成功 4失败 */
const API_STATUS_FAIL = 4;

function toNum(value: unknown, fallback = 0): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

/** 状态开关是否可操作：true/1/"true" → 可点；false/0/"false" → 禁用；缺省按可操作 */
function resolveSyncOperable(value: unknown): boolean {
    if (value === false || value === 0 || value === "0" || value === "false") return false;
    if (value === true || value === 1 || value === "1" || value === "true") return true;
    return true;
}

/** 预警库存 ← warehouseCodes[].warehouses[].warningStock（取首个有效值） */
function resolveWarehouseWarningStock(warehouses: DfSellableListWarehouseApi[] | undefined): number | undefined {
    for (const wh of warehouses ?? []) {
        if (wh.warningStock == null) continue;
        const n = Number(wh.warningStock);
        if (Number.isFinite(n)) return n;
    }
    return undefined;
}

/** 可售库存百分比 ← warehouseCodes[].warehouses[].percent（取首个有效值） */
function resolveWarehousePercent(warehouses: DfSellableListWarehouseApi[] | undefined): number | undefined {
    for (const wh of warehouses ?? []) {
        if (wh.percent == null) continue;
        const n = Number(wh.percent);
        if (Number.isFinite(n)) return n;
    }
    return undefined;
}

/** 组合库存模式 ← warehouseCodes[].warehouses[].comboStockMode（取首个有效值） */
function resolveWarehouseComboStockMode(warehouses: DfSellableListWarehouseApi[] | undefined): number | undefined {
    for (const wh of warehouses ?? []) {
        if (wh.comboStockMode == null) continue;
        const n = Number(wh.comboStockMode);
        if (Number.isFinite(n)) return n;
    }
    return undefined;
}

/** 产品行兜底：扫全部仓码下 warehouses[].warningStock；兼容旧最外层 / warehouseCodes.warningStock */
function resolveItemWarningStock(item: DfSellableListItemApi): number | undefined {
    for (const entry of resolveWarehouseCodeEntries(item)) {
        const fromWh = resolveWarehouseWarningStock(entry.warehouses);
        if (fromWh != null) return fromWh;
        if (entry.warningStock != null && Number.isFinite(Number(entry.warningStock))) {
            return Number(entry.warningStock);
        }
    }
    if (item.warningStock == null) return undefined;
    const n = Number(item.warningStock);
    return Number.isFinite(n) ? n : undefined;
}

/** 产品行兜底：扫全部仓码下 warehouses[].percent；兼容旧 warehouseCodes / items.percent */
function resolveItemPercent(item: DfSellableListItemApi): number | undefined {
    for (const entry of resolveWarehouseCodeEntries(item)) {
        const fromWh = resolveWarehousePercent(entry.warehouses);
        if (fromWh != null) return fromWh;
        if (entry.percent != null && Number.isFinite(Number(entry.percent))) {
            return Number(entry.percent);
        }
    }
    if (item.percent == null) return undefined;
    const n = Number(item.percent);
    return Number.isFinite(n) ? n : undefined;
}

/** 产品行兜底：扫全部仓码下 warehouses[].comboStockMode；兼容旧 items.comboStockMode */
function resolveItemComboStockMode(item: DfSellableListItemApi): number | undefined {
    for (const entry of resolveWarehouseCodeEntries(item)) {
        const fromWh = resolveWarehouseComboStockMode(entry.warehouses);
        if (fromWh != null) return fromWh;
    }
    if (item.comboStockMode == null) return undefined;
    const n = Number(item.comboStockMode);
    return Number.isFinite(n) ? n : undefined;
}

function resolveBundleChildQty(child: Pick<DfSellableListChildApi, "comboStockNum" | "qty">): number {
    // 数量 ← children[].comboStockNum（优先）；兼容旧字段 qty
    const fromCombo = Number(child.comboStockNum);
    if (Number.isFinite(fromCombo) && fromCombo >= 0) return Math.trunc(fromCombo);
    const fromQty = Number(child.qty);
    if (Number.isFinite(fromQty) && fromQty >= 0) return Math.trunc(fromQty);
    return 1;
}

function mapBundleChildren(children: DfSellableListChildApi[] | undefined): DfBundleChildItem[] | undefined {
    if (!children?.length) return undefined;
    return children.map((child, index) => ({
        id: String(child.productId ?? `${child.sku1p || child.skuIn1p || "child"}-${index}`),
        sku1p: String(child.sku1p || child.skuIn1p || "").trim() || "--",
        productName: String(child.productName || "").trim() || "--",
        qty: resolveBundleChildQty(child),
    }));
}

/** 失败态写入 qtyError；单元格状态文案优先用 apiStatusLabel */
function resolveQtyError(entry: Pick<DfSellableListWarehouseCodeApi, "apiStatus" | "apiStatusLabel" | "updateFailReason">): string | undefined {
    if (toNum(entry.apiStatus) !== API_STATUS_FAIL) return undefined;
    const reason = String(entry.updateFailReason || "").trim();
    if (reason) return reason;
    const label = String(entry.apiStatusLabel || "").trim();
    return label || undefined;
}

function resolvePlatformUpdateTime(entry: Pick<DfSellableListWarehouseCodeApi, "platformUpdateTimeLabel">): string {
    return String(entry.platformUpdateTimeLabel || "").trim() || "--";
}

/** ASIN 抑制信息仅取 warehouseCodes[].extractedSuppressedJson，不用最外层 */
function mapWarehouseCodeSuppressedTips(entry: DfSellableListWarehouseCodeApi): Array<{ type: string; tip: string }> {
    const list = Array.isArray(entry.extractedSuppressedJson) ? entry.extractedSuppressedJson : [];
    return list
        .map(item => ({
            type: String(item?.type ?? "").trim(),
            tip: String(item?.tip ?? "").trim(),
        }))
        .filter(item => item.type || item.tip);
}

function resolveWarehouseCodeStatus(entry: DfSellableListWarehouseCodeApi, tips: Array<{ type: string; tip: string }>): string {
    const status = String(entry.status ?? "").trim();
    if (status) return status;
    if (tips[0]?.type) return tips[0].type;
    return String(entry.suppressedText ?? "").trim();
}

function resolveWarehouseCodeTip(entry: DfSellableListWarehouseCodeApi, tips: Array<{ type: string; tip: string }>): string {
    const tip = String(entry.tip ?? "").trim();
    if (tip) return tip;
    return tips
        .map(item => [item.type, item.tip].filter(Boolean).join("\n"))
        .filter(Boolean)
        .join("\n\n");
}

function mapStockDetails(entry: DfSellableListWarehouseCodeApi, paused: boolean, switchDisabled: boolean): DfThirdPartyStockDetail[] {
    const warehouses = entry.warehouses ?? [];
    const dfCode = String(entry.warehouseCode || "").trim() || "--";
    const mappingId = entry.id;
    const platformUpdateTime = resolvePlatformUpdateTime(entry);
    const qtyError = resolveQtyError(entry);
    const details: DfThirdPartyStockDetail[] = [];

    for (const wh of warehouses) {
        // 兼容后端字段别名；按仓去重时名称必填
        const warehouseName =
            String(wh.systemWarehouseName || (wh as { warehouseName?: string }).warehouseName || (wh as { externalWarehouseName?: string }).externalWarehouseName || "").trim() || "--";
        const warehouseId = String(wh.id ?? "").trim();
        /** 列表「可用库存」← warehouseCodes[].warehouses[].matchedAvailableQty */
        const matchedAvailableQty = toNum(wh.matchedAvailableQty);
        // DF可售库存-X ← warehouseCodes[].warehouses[].customerStock
        const dfSellableX = toNum(wh.customerStock);
        // 预警库存 ← warehouseCodes[].warehouses[].warningStock
        const warningStock = wh.warningStock != null && Number.isFinite(Number(wh.warningStock)) ? Number(wh.warningStock) : undefined;
        // 可售库存百分比 ← warehouseCodes[].warehouses[].percent
        const percent = wh.percent != null && Number.isFinite(Number(wh.percent)) ? Number(wh.percent) : undefined;
        // 组合库存模式 ← warehouseCodes[].warehouses[].comboStockMode（1 组合 / 2 子品）
        const comboStockMode = wh.comboStockMode != null && Number.isFinite(Number(wh.comboStockMode)) ? Number(wh.comboStockMode) : undefined;
        // 列表「上次更新时间」← warehouseCodes[].platformUpdateTimeLabel
        // 海外仓 SKU ← warehouses[].children[].warehouseSku（不取仓库层 warehouseSku）
        const skuRows = (wh.children || []).filter(sku => String(sku.warehouseSku || "").trim());

        if (!skuRows.length) {
            details.push({
                warehouseName,
                dfCode,
                overseasSku: "--",
                skuQty: 0,
                availableQty: matchedAvailableQty,
                matchedAvailableQty,
                warningStock,
                preSaleQty: warningStock,
                percent,
                comboStockMode,
                hideDfCode: false,
                dfSellableX,
                mappingId,
                warehouseId,
                updateTime: platformUpdateTime === "--" ? undefined : platformUpdateTime,
                qtyError,
                paused,
                switchDisabled,
                apiStatus: toNum(entry.apiStatus),
                apiStatusLabel: String(entry.apiStatusLabel || "").trim(),
                updateFailReason: String(entry.updateFailReason || "").trim(),
            });
            continue;
        }

        skuRows.forEach((sku, idx) => {
            details.push({
                warehouseName,
                dfCode,
                overseasSku: String(sku.warehouseSku || "").trim() || "--",
                /** 海外仓 SKU x n ← children[].availableQty */
                skuQty: toNum(sku.availableQty),
                availableQty: toNum(sku.availableQty),
                matchedAvailableQty,
                warningStock,
                preSaleQty: warningStock,
                percent,
                comboStockMode,
                hideDfCode: idx > 0,
                dfSellableX,
                mappingId,
                warehouseId,
                updateTime: platformUpdateTime === "--" ? undefined : platformUpdateTime,
                qtyError,
                paused,
                switchDisabled,
                apiStatus: toNum(entry.apiStatus),
                apiStatusLabel: String(entry.apiStatusLabel || "").trim(),
                updateFailReason: String(entry.updateFailReason || "").trim(),
            });
        });
    }

    return details;
}

function mapWarehouseCodeZone(entry: DfSellableListWarehouseCodeApi, paused: boolean, switchDisabled: boolean): DfZoneBlock {
    const stockDetails = mapStockDetails(entry, paused, switchDisabled);
    const thirdPartyStock = (entry.warehouses ?? []).reduce((sum, wh) => sum + toNum(wh.matchedAvailableQty), 0);
    const platformStock = toNum(entry.platformStock);
    const updateTime = resolvePlatformUpdateTime(entry);
    const warehouseNames = [...new Set(stockDetails.map(d => d.warehouseName).filter(name => name && name !== "--"))];
    const qtyError = resolveQtyError(entry);
    const warningStock = resolveWarehouseWarningStock(entry.warehouses) ?? (entry.warningStock != null && Number.isFinite(Number(entry.warningStock)) ? Number(entry.warningStock) : undefined);
    const percent = resolveWarehousePercent(entry.warehouses) ?? (entry.percent != null && Number.isFinite(Number(entry.percent)) ? Number(entry.percent) : undefined);
    const comboStockMode = resolveWarehouseComboStockMode(entry.warehouses);
    const suppressedTips = mapWarehouseCodeSuppressedTips(entry);
    const mapping: DfCodeMapping = {
        id: entry.id,
        dfCode: String(entry.warehouseCode || "").trim() || "--",
        warehouseNames,
        amazonSellableQty: platformStock,
        updateTime,
        remark: qtyError,
        paused,
        switchDisabled,
        apiStatus: toNum(entry.apiStatus),
        apiStatusLabel: String(entry.apiStatusLabel || "").trim(),
        updateFailReason: String(entry.updateFailReason || "").trim(),
        status: resolveWarehouseCodeStatus(entry, suppressedTips) || undefined,
        tip: resolveWarehouseCodeTip(entry, suppressedTips) || undefined,
        suppressedTips: suppressedTips.length ? suppressedTips : undefined,
    };

    return {
        // 同一 zoneName 下可有多仓码；用 Y id 保证 zoneKey 唯一，避免改库存/日志找错 mapping
        zoneKey: String(entry.id || `${entry.zoneCode || ""}|${entry.warehouseCode || ""}`).trim() || "zone",
        zoneLabel: String(entry.zoneName || entry.zoneCode || "").trim() || "--",
        thirdPartyStock,
        currentSellableQty: platformStock,
        latestUpdateRelative: updateTime,
        mappings: [mapping],
        stockDetails,
        warningStock,
        preSaleQty: warningStock,
        percent,
        comboStockMode,
    };
}

/** 旧扁平 item → 单个 warehouseCode 条目（兼容） */
function toLegacyWarehouseCode(item: DfSellableListItemApi): DfSellableListWarehouseCodeApi | null {
    const hasFlat = item.warehouseCode != null || item.zoneCode != null || item.platformStock != null || (item.warehouses != null && item.warehouses.length > 0);
    if (!hasFlat) return null;
    return {
        id: item.id,
        warehouseCode: item.warehouseCode,
        zoneCode: item.zoneCode,
        zoneName: item.zoneName,
        platformStock: item.platformStock,
        warningStock: item.warningStock ?? item.warehouses?.[0]?.warningStock,
        apiStatus: item.apiStatus,
        apiStatusLabel: item.apiStatusLabel,
        updateFailReason: item.updateFailReason,
        platformUpdateTimeLabel: item.platformUpdateTimeLabel,
        lastPlatformPushAtLabel: item.lastPlatformPushAtLabel,
        warehouses: item.warehouses,
    };
}

function resolveWarehouseCodeEntries(item: DfSellableListItemApi): DfSellableListWarehouseCodeApi[] {
    if (item.warehouseCodes?.length) return item.warehouseCodes;
    const legacy = toLegacyWarehouseCode(item);
    return legacy ? [legacy] : [];
}

/** API VC SKU 组 → 表格产品行（多 warehouseCodes → 多 zone） */
export function mapDfSellableListItem(item: DfSellableListItemApi): DfSellableProductRow {
    const paused = Number(item.syncStatus) === 0;
    // 状态开关仅看列表 syncOperable：true 可点，false 不可编辑
    const syncOperable = resolveSyncOperable(item.syncOperable);
    const switchDisabled = !syncOperable;
    const zones = resolveWarehouseCodeEntries(item).map(entry => mapWarehouseCodeZone(entry, paused, switchDisabled));
    const thirdPartyStock = zones.reduce((sum, z) => sum + (Number(z.thirdPartyStock) || 0), 0);
    const currentSellableQty = zones.reduce((sum, z) => sum + (Number(z.currentSellableQty) || 0), 0);
    const hasStock = thirdPartyStock > 0 || currentSellableQty > 0;
    const statusRemark = String(item.statusRemark || "").trim();
    const image = String(item.imageLink || "").trim();
    const productType = item.productType != null && Number.isFinite(Number(item.productType)) ? Number(item.productType) : undefined;
    const latestUpdateRelative = zones.map(z => z.latestUpdateRelative).find(t => t && t !== "--") || "--";

    const productId = item.productId;
    // 列表「预警库存」← warehouseCodes[].warehouses[].warningStock（产品行兜底扫首个有效值）
    const warningStock = resolveItemWarningStock(item);
    // 可售库存百分比 ← warehouseCodes[].warehouses[].percent（产品行兜底扫首个有效值）
    const percent = resolveItemPercent(item);
    // 组合库存模式 ← warehouseCodes[].warehouses[].comboStockMode（优先仓级；兼容 items.comboStockMode）
    const comboStockMode = resolveItemComboStockMode(item);
    const ratio = percent != null ? Math.min(100, Math.max(0, percent)) / 100 : DEFAULT_DF_SELLABLE_RATIO;

    return {
        id: item.id,
        productId,
        image: image || undefined,
        productName: String(item.productName || "").trim() || "--",
        sku1p: String(item.sku1p || "").trim() || "--",
        productType,
        productTypeLabel: String(item.productTypeLabel || "").trim() || undefined,
        bundleChildren: mapBundleChildren(item.children),
        corpName: String(item.corpName || "").trim() || "--",
        ownerName: String(item.ownerUserName || "").trim() || undefined,
        asin: String(item.asin || "").trim() || "--",
        vcSku: String(item.vcSku || item.msku || "").trim() || undefined,
        countryCode: String(item.countryCode || "").trim() || undefined,
        thirdPartyStock,
        currentSellableQty,
        latestUpdateRelative,
        zoneStockLevel: hasStock ? 1 : 0,
        zones,
        calcRule: { label: "", ratio },
        syncOperable,
        syncDisabled: switchDisabled,
        exceptionInfo: statusRemark || undefined,
        warningStock,
        percent,
        comboStockMode,
        avgDailyOrders30d: 0,
        stockTrend: "flat",
        updateFrequency: "medium",
    };
}
